// types
import { TurboStripAnimation } from "@core-ui/interfaces/index";
import { AnimationState } from "./interface";

// hooks
import { useEffect, useRef, useState } from "react";

// constants
import { DISPLAY_DURATION, ANIMATION_DURATION } from "./constants";

// utils
import { isEmpty } from "@swiggy-private/js-utils";

interface UseShowAnimationProps {
    isNewBanner: boolean;
    animation: TurboStripAnimation | undefined;
    stripMsg: string;
}

const initialAnimationState: AnimationState = {
    currentMessageIndex: 0,
    nextMessageIndex: 1,
    cycleCount: 0,
    hasCompletedAnimation: false,
    isExiting: false,
    isActive: true,
    isFinalAnimation: false,
};

export const useShowAnimation = (props: UseShowAnimationProps) => {
    const { isNewBanner, animation, stripMsg } = props;
    const [animationState, setAnimationState] = useState<AnimationState>(initialAnimationState);
    const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const {
        currentMessageIndex,
        nextMessageIndex,
        cycleCount,
        hasCompletedAnimation,
        isExiting,
        isActive,
        isFinalAnimation,
    } = animationState;

    const shouldAnimate =
        isNewBanner &&
        !isEmpty(animation) &&
        !isEmpty(animation?.messageList) &&
        !hasCompletedAnimation &&
        animation?.messageList?.length > 1 &&
        animation?.frequency >= 1;

    const currentMessage = shouldAnimate ? animation?.messageList[currentMessageIndex] : stripMsg;
    const nextMessage = shouldAnimate && animation?.messageList[nextMessageIndex];

    useEffect(() => {
        if (!shouldAnimate || !animation) return;

        const timeoutRef = { current: 0 as ReturnType<typeof setTimeout> | 0 };
        const animationCycle = () => {
            // Start exit animation
            setAnimationState(prev => ({
                ...prev,
                isExiting: true,
                isActive: false,
            }));

            timeoutRef.current = setTimeout(() => {
                setAnimationState(prev => {
                    const nextIndex = (prev.currentMessageIndex + 1) % animation.messageList.length;
                    const newCycleCount = nextIndex === 0 ? prev.cycleCount + 1 : prev.cycleCount;
                    const isFinalCycle = newCycleCount >= animation.frequency;

                    return {
                        ...prev,
                        currentMessageIndex: nextIndex,
                        nextMessageIndex: (nextIndex + 1) % animation.messageList.length,
                        cycleCount: newCycleCount,
                        isExiting: false,
                        isActive: true,
                        isFinalAnimation: isFinalCycle && nextIndex === 0,
                    };
                });
            }, ANIMATION_DURATION);
        };

        // Start the animation cycle immediately
        const interval = setInterval(animationCycle, ANIMATION_DURATION + DISPLAY_DURATION);

        // Cleanup on unmount
        return () => {
            clearInterval(interval);
            clearTimeout(timeoutRef.current);
        };
    }, [shouldAnimate, animation]);

    useEffect(() => {
        if (!shouldAnimate || !animation) return;

        if (cycleCount >= animation.frequency && isFinalAnimation) {
            // Wait for the final animation to complete before setting hasCompletedAnimation
            timeoutIdRef.current = setTimeout(() => {
                setAnimationState(prev => ({
                    ...prev,
                    hasCompletedAnimation: true,
                    currentMessageIndex: 0,
                    nextMessageIndex: 1,
                    isExiting: false,
                    isActive: true,
                }));
            }, ANIMATION_DURATION);

            return () => {
                if (timeoutIdRef.current) {
                    clearTimeout(timeoutIdRef.current);
                }
            };
        }
    }, [cycleCount, animation, shouldAnimate, isFinalAnimation]);

    return {
        hasCompletedAnimation,
        isExiting,
        isActive,
        currentMessage,
        nextMessage,
        shouldAnimate,
    };
};
